import { uuidv4 } from '@wconcept/wconcept-common/utils/commonUtils';
import { useCallback } from 'react';
import crypto from 'crypto';
import { getCookieWckDisplayMktSID } from '@wconcept/wconcept-common/utils/cookie';

const BYTEPLUS_TOKEN = '0262c4952dc284486fd0c42cbbcb8388';
const BYTEPLUS_PROJECT_ID = 'w_concept';
const BYTEPLUS_ACK_SERVER_IMPRESSIONS_ENDPOINT =
  'https://airpremium.ap-southeast-1.bytepluses.com/RetailSaaS/AckServerImpressions';
const BYTEPLUS_WRITE_USER_EVENTS_ENDPOINT =
  'https://airpremium.ap-southeast-1.bytepluses.com/RetailSaaS/WriteUserEvents';

interface AckServerImpressionsBody {
  project_id?: 'w_concept';
  model_id?: 'video_play' | 'video_play';
  predict_request_id?: string;
  user_id?: string;
  traffic_source?: string;
  scene?: string;
  altered_products?: {
    product_id: string;
    altered_reason: string;
    rank: number;
  }[];
  extra?: string;
}

interface WriteUserEventsBody {
  project_id?: 'w_concept';
  stage?: 'production' | 'Incremental_sync_streaming';
  data?: {
    user_id?: string;
    event_type?: string;
    event_timestamp?: number;
    product_id?: string;
    scene_name?: string;
    offset?: number;
    platform?: string;
    item_type?: string;
    content_id?: string;
    page_number?: number;
    stay_duration?: number;
  }[];
  extra?: string;
}

interface SignatureResult {
  signature: string;
  tenantTs: string;
  tenantNonce: string;
  error?: string;
}

interface SignedRequestResult {
  headers: {
    'Content-Type': string;
    'Tenant-Id': string;
    'Tenant-Ts': string;
    'Tenant-Nonce': string;
    'Tenant-Signature': string;
    'Request-Id': string;
  };
  body: string;
}

const useByteplusLogger = () => {
  /**
   * 바이트 배열을 16진수 문자열로 변환
   * @param {Uint8Array} bytes - 변환할 바이트 배열
   * @returns {string} 16진수 문자열
   */
  const bytes2Hex = useCallback(
    (bytes: Uint8Array): string =>
      Array.from(bytes)
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join(''),
    []
  );

  /**
   * 제공된 파라미터에 기반하여 서명 계산
   * @param {string} token - API 토큰
   * @param {string} tenantId - 테넌트 ID
   * @param {string} tenantTs - 타임스탬프
   * @param {string} tenantNonce - 난수 문자열
   * @param {Object} bodyObj - 서명할 본문 객체
   * @returns {Promise<SignatureResult>} 계산된 서명과 관련 정보
   */
  const calculateSignature = useCallback(
    async (
      token: string,
      tenantId: string,
      tenantTs: string = Math.floor(Date.now() / 1000).toString(),
      tenantNonce: string = uuidv4(),
      bodyObj: AckServerImpressionsBody = {}
    ): Promise<SignatureResult> => {
      try {
        // 본문 객체를 JSON 문자열로 변환
        const httpBody = JSON.stringify(bodyObj);
        // 인코딩할 텍스트 배열 생성
        const encoder = new TextEncoder();
        const data = encoder.encode(
          token + httpBody + tenantId + tenantTs + tenantNonce
        );

        // Web Crypto API를 사용하여 SHA-256 해시 계산
        const hashBuffer = crypto.createHash('sha256').update(data).digest();

        return {
          signature: bytes2Hex(new Uint8Array(hashBuffer)),
          tenantTs,
          tenantNonce,
        };
      } catch (err: any) {
        const errorMessage = err.message || '서명 계산 오류';
        console.error('서명 계산 오류:', err);
        return {
          signature: '',
          tenantTs,
          tenantNonce,
          error: errorMessage,
        };
      }
    },
    [bytes2Hex]
  );

  const encryptUserId = (userId: string) => {
    const isLogin = userId && userId !== '0';
    // 사용자 ID가 없거나 '0'인 경우 'wconcept' 사용
    const formattedUserId = isLogin ? `W!b7${userId}*` : 'wconcept';

    if (!isLogin) {
      return 'wconcept';
    }
    return crypto.createHash('sha256').update(formattedUserId).digest('hex');
  };

  /**
   * 필요한 서명 헤더가 포함된 요청 생성
   * @param {string} token - API 토큰
   * @param {string} tenantId - 테넌트 ID
   * @param {Object} bodyObj - 서명할 본문 객체
   * @returns {Promise<SignedRequestResult>} 요청에 대한 헤더와 본문
   */
  const generateSignedRequest = useCallback(
    async (
      token: string,
      tenantId: string,
      bodyObj: AckServerImpressionsBody | WriteUserEventsBody = {}
    ): Promise<SignedRequestResult> => {
      const tenantTs = Math.floor(Date.now() / 1000).toString();
      const tenantNonce = uuidv4();

      const { signature } = await calculateSignature(
        token,
        tenantId,
        tenantTs,
        tenantNonce,
        bodyObj
      );

      return {
        headers: {
          'Content-Type': 'application/json',
          'Tenant-Id': tenantId,
          'Tenant-Ts': tenantTs,
          'Tenant-Nonce': tenantNonce,
          'Tenant-Signature': signature,
          'Request-Id': tenantNonce,
        },
        body: JSON.stringify(bodyObj),
      };
    },
    [calculateSignature]
  );

  /**
   * BytePlus 서버 노출 이벤트 로깅
   * @param {AckServerImpressionsBody} body - 서버 노출 이벤트 데이터
   * @returns {Promise<Response>} 페치 응답
   */
  const ackServerImpressions = useCallback(
    async (body: AckServerImpressionsBody): Promise<Response> => {
      const userId = encryptUserId(body?.user_id || '');
      const productId = BYTEPLUS_PROJECT_ID;

      const updatedBodyObj = {
        ...body,
        project_id: productId,
        user_id: userId,
      } as AckServerImpressionsBody;

      const signedRequest = await generateSignedRequest(
        BYTEPLUS_TOKEN,
        process.env.NEXT_PUBLIC_BYTEPLUS_APP_ID as string,
        updatedBodyObj
      );

      return fetch(BYTEPLUS_ACK_SERVER_IMPRESSIONS_ENDPOINT, {
        method: 'POST',
        headers: signedRequest.headers,
        body: signedRequest.body,
        credentials: 'omit',
        keepalive: true,
      });
    },
    [generateSignedRequest]
  );

  /**
   * BytePlus 유저 행동 이벤트 로깅
   * @param {WriteUserEventsBody} body - 유저 행동 이벤트 데이터
   * @returns {Promise<Response>} 페치 응답
   */
  const writeUserEvents = useCallback(
    async (body: WriteUserEventsBody): Promise<Response> => {
      const productId = BYTEPLUS_PROJECT_ID;
      const sessionId = await getCookieWckDisplayMktSID();
      const updatedBodyObj = {
        ...body,
        data: body.data
          ?.map(item => ({
            ...item,
            user_id:
              item.user_id !== '0'
                ? encryptUserId(item?.user_id || '')
                : encodeURIComponent(sessionId || ''),
          }))
          .map(item => JSON.stringify(item)),
        project_id: productId,
        stage: 'production',
      } as any as WriteUserEventsBody;

      const signedRequest = await generateSignedRequest(
        BYTEPLUS_TOKEN,
        process.env.NEXT_PUBLIC_BYTEPLUS_APP_ID as string,
        updatedBodyObj
      );

      return fetch(BYTEPLUS_WRITE_USER_EVENTS_ENDPOINT, {
        method: 'POST',
        headers: signedRequest.headers,
        body: signedRequest.body,
        credentials: 'omit',
        keepalive: true,
      });
    },
    [generateSignedRequest]
  );

  return {
    calculateSignature,
    generateSignedRequest,
    ackServerImpressions,
    writeUserEvents,
  };
};

export default useByteplusLogger;
